import { Box, Pagination, Paper, TextField, InputAdornment, Typography } from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import OrganizationListComponent from "../components/organization-list.component";
import CustomHeader from "../../../core/components/common/custom-header.component";
import OrganizationSkeletonLoader from "../skeleton/organization.skeleton";
import { useOrg } from "../hooks/userOrg";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import Select from "react-select";
import theme from "../../../core/components/layout/theme";
import CloseIcon from "@mui/icons-material/Close";
import { RBACUIGuard } from "../../RBACPlugin";
import { permissions } from "../../../core/components/common/variables/static_variables";
import { useNavigate } from "react-router-dom";


const customStyles = {
  control: (base, state) => ({
    ...base,
    height: 42,
    backgroundColor: "transparent",
    width: 250,
    fontSize: 14,
    borderColor: state.isFocused ? theme.palette.primary.main : "#dce8f5",
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.palette.primary.main}` : "none",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 12,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 99999,
    fontSize: 14,
  }),
};

export default function OrganizationListPage() {
  const {
    listOrg,
    orgList,
    loading,
    viewOrgSubscription,
    orgSubscriptionDetails,
    filterListOrg,
    filterList,
  } = useOrg();

  const navigate = useNavigate();

  const rowsPerPage = 9;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedField, setSelectedField] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);

  const handleChangePagination = (event, page) => {
    setCurrentPage(page);
  };

  const handleFieldChange = (selectedOption) => {
    setSelectedField(selectedOption);
    setSearchValue("");
  };

  const handleInputChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSearch = () => {
    if (selectedField && searchValue) {
      const params = {
        searchFor: selectedField.value,
        searchVal: searchValue,
        page: 1,
        limit: rowsPerPage,
      };
      filterListOrg(params);
      setIsFiltering(true);
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const params = {
      page: currentPage,
      limit: rowsPerPage,
    };

    if (isFiltering && selectedField && searchValue) {
      filterListOrg({
        searchFor: selectedField.value,
        searchVal: searchValue,
        ...params,
      });
    } else {
      listOrg(params);
    }
  }, [currentPage]);

  useEffect(() => {
    if (isFiltering && filterList) {
      setTotalPages(filterList.totalPages || 0);
    } else if (orgList) {
      setTotalPages(orgList.totalPages || 0);
    }
  }, [orgList, filterList, isFiltering]);

  useEffect(() => {
    if (!selectedField || !searchValue) {
      listOrg({ page: 1, limit: rowsPerPage });
      setIsFiltering(false);
      setCurrentPage(1);
    }
  }, [selectedField, searchValue]);

  const searchForOptions = [
    { value: "org_name", label: "Organization Name" },
  ];

  return (
    <>
      <CustomTypography variant="h3" text={"Organization"} customStyles={{ marginBottom: 0 }} />
      <CustomTypography
        variant="subtitle1"
        text={"Easily manage organizational details, and create new organization records effortlessly."}
        customStyles={{ marginBottom: 0 }}
      />

      <Paper
        elevation={0}
        sx={{
          border: `1px solid #e5edf6`,
          borderRadius: 2,
          padding: "24px 32px",
          mt: 3,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "right", alignItems: "center" }}></Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }} gap={1}>
          <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>All Organizations</Typography>
          <Typography sx={{ color: "gray", fontWeight: 600 }}>
            {isFiltering ? filterList?.totalElements : orgList?.totalElements}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <RBACUIGuard requiredPermission={permissions.organizations.FILTER_LIST_ORGANIZATION}>
              <Select
                styles={customStyles}
                options={searchForOptions}
                value={selectedField}
                onChange={handleFieldChange}
                placeholder="Select filter"
              />
              &nbsp;&nbsp;
              {selectedField && (
                <TextField
                  placeholder={selectedField.label}
                  value={searchValue}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                      if (e.key === "Enter") {
                      handleSearch();
                      }
                  }}
                  sx={{
                      "& .MuiInputBase-root": {
                      height: 42,
                      backgroundColor: "transparent",
                      width: 300,
                      mr: 1,
                      },
                  }}
                  variant="outlined"
                  autoComplete="off"
                  InputProps={{
                      startAdornment: (
                      <InputAdornment position="start">
                          <Typography sx={{ fontSize: 12, fontStyle: "normal" }}>Search:</Typography>
                      </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                      <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                          <CloseIcon
                          fontSize="small"
                          onClick={() => {
                              setSearchValue("");
                              setSelectedField(null);
                          }}
                          />
                      </InputAdornment>
                      ),
                      style: {
                      padding: "10px",
                      fontSize: 14,
                      fontStyle: "italic",
                      },
                  }}
                  />

              )}
            </RBACUIGuard>
            &nbsp;&nbsp;
            <RBACUIGuard requiredPermission={permissions.organizations.CREATE_ORGANIZATION}>
              <CustomButton
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                label="Create Organization"
                customStyles={{ fontSize: 14 }}
                onClick={() => navigate("/organization/create")}
              />
            </RBACUIGuard>
          </Box>
        </Box>

        <Box sx={{ mt: 3, mb: 3 }}>
          {loading ? (
            <OrganizationSkeletonLoader />
          ) : (
            <>
              <OrganizationListComponent
                orgList={isFiltering ? filterList : orgList}
                loading={loading}
                viewOrgSubscription={viewOrgSubscription}
                orgSubscriptionDetails={orgSubscriptionDetails}
              />
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handleChangePagination}
                  sx={{
                    margin: "auto",
                    width: "fit-content",
                    paddingTop: 3,
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#0b99da",
                      color: "#fff",
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Paper>
    </>
  );
}
